import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { finalize, Observable, take, tap } from 'rxjs';

import { CatalogItem } from 'src/app/common/CatalogItem';
import { AsyncClickDirective } from 'src/app/directives/async-click.directive';
import { CallApiService } from 'src/app/services/api-action.service';
import { BookmarkService } from 'src/app/services/bookmark.service';
import { UserSettingsService } from 'src/app/services/user-settings.service';

import { CatalogItemCardComponent } from '../catalog-item-card/catalog-item-card.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/**
 * #652: the „Merkliste" — the listings the signed-in user bookmarked, newest first.
 *
 * Renders the same `app-catalog-item-card` as the search results rather than a second card
 * layout, and pages it the same way (#648 `PagedResult` + „Mehr laden", page size from
 * `UserSettingsService`). Behind `AuthGuard`, unlike `/search`: the list belongs to a
 * profile and the API is `[Authorize]` (decision 3a).
 *
 * Listings that no longer resolve — deactivated, deleted, out of the catalog (#509) — are
 * simply absent from the response; the page shows fewer entries, never an error tile.
 */
@Component({
  imports: [
    RouterModule,
    AsyncClickDirective,
    CatalogItemCardComponent,
    LoadingSpinnerComponent,
  ],
  selector: 'app-bookmark-list',
  templateUrl: './bookmark-list.component.html',
  styleUrls: ['./bookmark-list.component.css'],
})
export class BookmarkListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private bookmarkService = inject(BookmarkService);
  private apiActionService = inject(CallApiService);
  private userSettingsService = inject(UserSettingsService);

  public catalogItems: CatalogItem[] = [];
  public totalCount = 0;
  public loading = true;
  public loadingMore = false;
  public bookmarkedIds: ReadonlySet<string> = new Set<string>();

  private skip = 0;
  private pageSize = 50;

  ngOnInit(): void {
    this.bookmarkService.ensureIdsLoaded();
    this.bookmarkService.bookmarkedIds$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => (this.bookmarkedIds = ids));

    // #648: page size from the user's ListPageSize setting; listPageSize$ replays, so this
    // settles immediately after the first read.
    this.userSettingsService.listPageSize$.pipe(take(1)).subscribe((size) => {
      this.pageSize = size;
      this.fetchPage(false);
    });
  }

  public loadMore(): void {
    if (this.loadingMore) {
      return;
    }
    this.loadingMore = true;
    // The cursor is what is on screen, not skip + pageSize: removing a tile (below) makes
    // the two disagree, and the count is the one that stays right.
    this.skip = this.catalogItems.length;
    this.fetchPage(true);
  }

  /**
   * Un-bookmarking here also has to take the tile off THIS page — on the search results it
   * only changes an icon. Removed after the request succeeds, not optimistically: the icon
   * already gives the instant feedback, and a tile that vanished and came back would read
   * as a glitch. The service reverts its own id set on failure.
   */
  public bookmarkAction(catalogSkuId: string): () => Observable<unknown> {
    return () =>
      this.apiActionService.callWithToasts$(
        this.bookmarkService.toggle(catalogSkuId).pipe(
          tap(() => {
            if (!this.bookmarkService.isBookmarked(catalogSkuId)) {
              this.removeLocally(catalogSkuId);
            }
          })
        ),
        null,
        $localize`:@@catalog.bookmark.error:Die Merkliste konnte nicht geändert werden.`
      );
  }

  public clearAll(): Observable<unknown> {
    return this.apiActionService.confirmAndCallWithToasts$(
      this.bookmarkService.clearAll().pipe(
        tap(() => {
          this.catalogItems = [];
          this.totalCount = 0;
          this.skip = 0;
        })
      ),
      $localize`:@@bookmarkList.clear.confirm:Sollen wirklich alle gemerkten Artikel entfernt werden?`,
      $localize`:@@bookmarkList.clear.success:Die Merkliste wurde geleert.`,
      $localize`:@@bookmarkList.clear.error:Die Merkliste konnte nicht geleert werden.`
    );
  }

  private fetchPage(append: boolean): void {
    this.bookmarkService
      .getBookmarks({ skip: this.skip, take: this.pageSize })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
        })
      )
      .subscribe({
        next: (result) => {
          this.catalogItems = append ? [...this.catalogItems, ...result.items] : result.items;
          this.totalCount = result.totalCount;
        },
      });
  }

  private removeLocally(catalogSkuId: string): void {
    const before = this.catalogItems.length;
    this.catalogItems = this.catalogItems.filter((i) => i.catalogSku?.skuId !== catalogSkuId);
    // Keep the "x von y" line honest — the removed tile is gone from the server list too.
    this.totalCount = Math.max(0, this.totalCount - (before - this.catalogItems.length));
  }
}
